import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { ShoppingCart, Plus, Trash2, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ShoppingList() {
  const { getAuthHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newItem, setNewItem] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const response = await axios.get(`${API}/shopping-list`, { headers: getAuthHeaders() });
        setItems(response.data);
      } catch (error) {
        console.error('Error fetching shopping list:', error);
        toast.error('Failed to load shopping list');
      } finally {
        setLoading(false);
      }
    };
    fetchItems();
  }, [getAuthHeaders]);

  const addItem = async (e) => {
    e.preventDefault();
    if (!newItem.trim()) return;
    
    setAdding(true);
    try {
      const response = await axios.post(
        `${API}/shopping-list`,
        { item_name: newItem.trim() },
        { headers: getAuthHeaders() }
      );
      setItems(prev => [...prev, response.data]);
      setNewItem('');
      toast.success('Item added');
    } catch (error) {
      toast.error('Failed to add item');
    } finally {
      setAdding(false);
    }
  };

  const toggleItem = async (itemId) => {
    try {
      const response = await axios.patch(
        `${API}/shopping-list/${itemId}`,
        {},
        { headers: getAuthHeaders() }
      );
      setItems(prev => prev.map(item => 
        item.id === itemId ? { ...item, is_purchased: response.data.is_purchased } : item
      ));
    } catch (error) {
      toast.error('Failed to update item');
    }
  };

  const deleteItem = async (itemId) => {
    try {
      await axios.delete(`${API}/shopping-list/${itemId}`, { headers: getAuthHeaders() });
      setItems(prev => prev.filter(item => item.id !== itemId));
      toast.success('Item removed');
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const clearPurchased = async () => {
    const purchasedItems = items.filter(item => item.is_purchased);
    try {
      await Promise.all(
        purchasedItems.map(item => 
          axios.delete(`${API}/shopping-list/${item.id}`, { headers: getAuthHeaders() })
        )
      );
      setItems(prev => prev.filter(item => !item.is_purchased));
      toast.success('Cleared purchased items');
    } catch (error) {
      toast.error('Failed to clear items');
    }
  };

  const unpurchasedItems = items.filter(item => !item.is_purchased);
  const purchasedItems = items.filter(item => item.is_purchased);

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="shopping-list-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-bold text-[#F5F5F5] mb-3 flex items-center gap-3">
          <ShoppingCart className="w-10 h-10 text-[#10B981]" />
          Shopping List
        </h1>
        <p className="text-[#A3A3A3] text-lg">
          Track ingredients you need to buy
        </p>
      </div>

      {/* Add Item Form */}
      <form onSubmit={addItem} className="mb-8">
        <div className="flex gap-3">
          <Input
            type="text"
            placeholder="Add an item..."
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            className="flex-1 bg-[#121212] border-white/10 focus:border-[#D4AF37] text-[#F5F5F5] placeholder:text-[#525252] rounded-xl"
            data-testid="add-item-input"
          />
          <Button
            type="submit"
            disabled={adding || !newItem.trim()}
            className="bg-[#10B981] text-white hover:bg-[#059669] rounded-xl px-6 btn-press"
            data-testid="add-item-btn"
          >
            {adding ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Plus className="w-5 h-5" />
            )}
          </Button>
        </div>
      </form>

      {/* Shopping List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-16 shimmer rounded-xl" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="empty-state glass rounded-2xl py-16">
          <ShoppingCart className="w-16 h-16 text-[#525252] mb-6" />
          <h3 className="text-xl font-bold text-[#F5F5F5] mb-2">Your list is empty</h3>
          <p className="text-[#A3A3A3] max-w-md">
            Add items from cocktail recipes or type them manually above
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Unpurchased Items */}
          {unpurchasedItems.length > 0 && (
            <div>
              <h2 className="text-sm font-medium text-[#A3A3A3] uppercase tracking-wide mb-4">
                To Buy ({unpurchasedItems.length})
              </h2>
              <div className="space-y-2">
                {unpurchasedItems.map((item, index) => (
                  <div
                    key={item.id}
                    className="shopping-item animate-fadeIn"
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <Checkbox
                      checked={false}
                      onCheckedChange={() => toggleItem(item.id)}
                      className="border-[#D4AF37] data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                      data-testid={`checkbox-${item.id}`}
                    />
                    <span className="item-name flex-1 text-[#F5F5F5] capitalize">
                      {item.item_name}
                    </span>
                    {item.quantity && item.quantity !== '1' && (
                      <span className="text-[#525252] text-sm">x{item.quantity}</span>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteItem(item.id)}
                      className="text-[#525252] hover:text-[#EF4444] opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`delete-item-btn-${item.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Purchased Items */}
          {purchasedItems.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-medium text-[#A3A3A3] uppercase tracking-wide">
                  Purchased ({purchasedItems.length})
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearPurchased}
                  className="text-[#EF4444] hover:text-[#DC2626] text-xs"
                  data-testid="clear-purchased-btn"
                >
                  <X className="w-3 h-3 mr-1" />
                  Clear All
                </Button>
              </div>
              <div className="space-y-2">
                {purchasedItems.map((item, index) => (
                  <div
                    key={item.id}
                    className="shopping-item purchased animate-fadeIn"
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <Checkbox
                      checked={true}
                      onCheckedChange={() => toggleItem(item.id)}
                      className="border-[#10B981] data-[state=checked]:bg-[#10B981] data-[state=checked]:border-[#10B981]"
                      data-testid={`checkbox-${item.id}`}
                    />
                    <span className="item-name flex-1 text-[#525252] capitalize line-through">
                      {item.item_name}
                    </span>
                    <Check className="w-4 h-4 text-[#10B981]" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Summary */}
      {items.length > 0 && (
        <div className="mt-8 glass rounded-2xl p-6">
          <div className="flex items-center justify-between text-sm">
            <span className="text-[#A3A3A3]">Progress</span>
            <span className="text-[#F5F5F5] font-medium">
              {purchasedItems.length} / {items.length} items
            </span>
          </div>
          <div className="mt-3 h-2 bg-[#1A1A1A] rounded-full overflow-hidden">
            <div
              className="h-full bg-[#10B981] rounded-full transition-all duration-500"
              style={{ width: `${(purchasedItems.length / items.length) * 100}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
